<template>
    <div style="padding: 10px">
        <MainLayout :hasLeft="false" :hasRight="false" :hasHeader="false">
            <div slot="main" class="workbench-main">
                <div class="workbench-data-content">
                    <!-- 搜索区域 -->
                    <div class="data-search">
                        <SearchTop class="SearchTop" :changeSearch="handleSearchChange" :typeNum="4"
                            :siteArr="siteList" />
                    </div>

                    <!-- 业务概览 -->
                    <div class="data-cell">
                        <DataTitle :title="$t('wuliu.workbench.businessOverview')" />
                        <div class="data-statistics">
                            <div class="statistics-item" :class="'overview-bg-' + (index + 1)"
                                v-for="(item, index) in efficiencyList" :key="item.name">
                                <div class="statistics-txt">{{ item.name }}</div>
                                <div class="statistics-value"> {{ item.type === 'time' ? formatMinutesToHMS(item.value)
                                    :
                                    item.value
                                }} {{ item.unit }}</div>
                            </div>
                        </div>

                        <!-- 图表区域（配置化组件） -->
                        <TrendChartGroup :chartList="chartList" />
                    </div>

                    <!-- 配送作业数据 -->
                    <div class="data-cell">
                        <DataTitle :title="$t('wuliu.workbench.deliveryData')" />
                        <ProgressChart :deliveryList="deliveryList" />
                    </div>
                </div>
            </div>
        </MainLayout>
    </div>

</template>

<script>
import { debounce } from 'lodash';
import MainLayout from '@/components/layout';
import SearchTop from './components/SearchTop.vue';
import TrendChartGroup from './components/TrendChartGroup.vue';
import DataTitle from './components/DataTitle.vue';
import ProgressChart from './components/progressChart.vue';

import SiteApi from '@/page/business/wuliu/api/site';
import totalApi from './api/index';

// 图表数据配置映射
let CHART_DATA_CONFIG = {
    fee: { dayKey: 'avgDayDeliveryFee', totalKey: 'totalDeliveryFee' },
    amount: { dayKey: 'avgDayDeliveryAmount', totalKey: 'totalDeliveryAmount' },
    userNum: { dayKey: 'avgDayUserNum', totalKey: 'totalUserNum' },
    orderItemNum: { dayKey: 'avgDayOrderItemNum', totalKey: 'totalOrderItemNum' },

};

export default {
    name: 'Workbench',
    components: {
        MainLayout,
        SearchTop,
        TrendChartGroup,
        DataTitle,
        ProgressChart,
    },
    data () {
        document.title = this.$t('wuliu.workbench.pageTitle');
        return {
            siteList: [], // 配送站点列表
            searchObj: {
                beginTime: '',
                endTime: '',
                siteId: '',
            },
            // 初始化效率统计数据（基于配置）
            efficiencyList: this.getEfficiencyConfig().map((item) => ({
                ...item,
                value: 0,
            })),
            // 图表数据（结构统一化）
            chartData: {
                orderItemNum: { dayNum: 0, totalNum: 0, data: {} },
                amount: { dayNum: 0, totalNum: 0, data: {} },
                userNum: { dayNum: 0, totalNum: 0, data: {} },
                fee: { dayNum: 0, totalNum: 0, data: {} },
            },
            // 初始化配送作业数据（基于配置）
            deliveryList: this.getDeliveryConfig().map((config) => ({
                title: config.title,
                data: config.fields.map((field) => ({
                    name: field.name,
                    value: 0,
                    unit: field.unit,
                })),
            })),
        };
    },
    computed: {
        // 动态生成图表配置列表
        chartList () {
            return Object.keys(this.getChartConfigMap()).map(type => ({
                ...this.getChartConfigMap()[type],
                type,
                data: this.chartData[type],
            }));
        },
    },
    methods: {
        // 配送作业数据配置映射
        getDeliveryConfig () {
            return [
                { title: this.$t('wuliu.workbench.deliveryCount'), fields: [{ name: this.$t('wuliu.workbench.dailyAvg'), key: 'avgDayOrderNum', unit: '' }, { name: this.$t('wuliu.workbench.total'), key: 'totalOrderNum', unit: '' }] },
                { title: this.$t('wuliu.workbench.flightDistance'), fields: [{ name: this.$t('wuliu.workbench.dailyAvg'), key: 'avgDayDistance', unit: 'km' }, { name: this.$t('wuliu.workbench.total'), key: 'totalDistance', unit: 'km' }] },
                { title: this.$t('wuliu.workbench.flightDuration'), fields: [{ name: this.$t('wuliu.workbench.dailyAvg'), key: 'avgDayDuration', unit: 'min' }, { name: this.$t('wuliu.workbench.total'), key: 'totalDuration', unit: 'min' }] },
                { title: this.$t('wuliu.workbench.cargoWeight'), fields: [{ name: this.$t('wuliu.workbench.dailyAvg'), key: 'avgDayWeight', unit: 'kg' }, { name: this.$t('wuliu.workbench.total'), key: 'totalWeight', unit: 'kg' }] },
            ];
        },

        // 效率统计数据配置映射
        getEfficiencyConfig () {
            return [
                { name: this.$t('wuliu.workbench.fastestDelivery'), key: 'fastestDeliveryTime', unit: '', type: 'time' },
                { name: this.$t('wuliu.workbench.avgDeliveryTime'), key: 'avgDeliveryTime', unit: '', type: 'time' },
                { name: this.$t('wuliu.workbench.maxOrdersPerFlight'), key: 'maxOrderItemNum', unit: '' },
                { name: this.$t('wuliu.workbench.avgOrdersPerFlight'), key: 'avgOrderItemNum', unit: '' },
                { name: this.$t('wuliu.workbench.repurchaseRate'), key: 'repurchaseRate', unit: '%' },
            ];
        },

        // 图表配置映射（元信息）
        getChartConfigMap () {
            return {
                fee: { title: this.$t('wuliu.workbench.deliveryFee'), prefix: '￥', suffix: '' }, // 配送费
                amount: { title: this.$t('wuliu.workbench.orderAmount'), prefix: '￥', suffix: '' }, // 外卖金额
                userNum: { title: this.$t('wuliu.workbench.userNum'), prefix: '', suffix: this.$t('wuliu.workbench.users') }, // 服务用户数
                orderItemNum: { title: this.$t('wuliu.workbench.orderCount'), prefix: '', suffix: this.$t('wuliu.workbench.orders') }, // 外卖订单
            };
        },

        // 搜索条件变化（防抖处理）
        handleSearchChange: debounce(function (val) {
            this.searchObj = { ...val };
            this.resetData();
            this.fetchAllData();
        }, 300),

        // 批量请求所有数据
        async fetchAllData () {
            try {
                await Promise.all([
                    this.getStatisticsTotalData(),
                    this.getStatisticsEfficiency(),
                    this.getAllLineData(),
                ]);
            } catch (error) {
                console.error('数据请求失败:', error);
                this.$message.error(this.$t('wuliu.workbench.loadDataFailed'));
            }
        },

        // 重置所有数据
        resetData () {
            // 重置效率统计数据
            this.efficiencyList = this.getEfficiencyConfig().map((item) => ({
                ...item,
                value: 0,
            }));
            // 重置配送作业数据
            this.deliveryList = this.getDeliveryConfig().map((config) => ({
                title: config.title,
                data: config.fields.map((field) => ({ ...field, value: 0 })),
            }));
            // 重置图表数据
            Object.keys(this.chartData).forEach((key) => {
                this.chartData[key] = { dayNum: 0, totalNum: 0, data: {} };
            });
        },

        // 批量获取图表趋势数据
        getAllLineData () {
            const chartTypes = Object.keys(this.getChartConfigMap());

            return Promise.all(
                chartTypes.map(type => this.getStatisticsLineChart(type)),
            );
        },

        // 获取站点列表
        async getDeliverySiteList () {
            try {
                const res = await SiteApi.fetchSiteList({ type: 'T' });
                this.siteList = res.code === 200 ? res.rows || [] : [];
                if (res.code !== 200) {
                    this.$message.error(res.msg || this.$t('wuliu.workbench.fetchSiteFailed'));
                }
            } catch (error) {
                this.siteList = [];
                console.error('获取站点列表异常:', error);
                this.$message.error(this.$t('wuliu.workbench.fetchSiteListFailed'));
            }
        },

        // 获取业务概览统计数据（配置化生成）
        async getStatisticsTotalData () {
            try {
                const res = await totalApi.statisticsTotalData({ ...this.searchObj });
                if (res.code !== 200) {
                    this.$message.error(res.msg || this.$t('wuliu.workbench.fetchOverviewFailed'));
                    return;
                }

                const stats = res.data || {};

                // 1. 配置化生成配送作业数据
                this.deliveryList = this.getDeliveryConfig().map((config) => ({
                    title: config.title,
                    data: config.fields.map((field) => ({
                        name: field.name,
                        value: stats[field.key] || 0,
                        unit: field.unit,
                    })),
                }));

                // 2. 配置化更新图表头部数据
                Object.keys(CHART_DATA_CONFIG).forEach((chartKey) => {
                    const { dayKey, totalKey } = CHART_DATA_CONFIG[chartKey];

                    this.chartData[chartKey] = {
                        ...this.chartData[chartKey],
                        dayNum: stats[dayKey] || 0,
                        totalNum: stats[totalKey] || 0,
                    };
                });
            } catch (error) {
                console.error('业务概览数据异常:', error);
                this.$message.error(this.$t('wuliu.workbench.fetchOverviewRetry'));
            }
        },

        // 获取配送效率数据（配置化生成）
        async getStatisticsEfficiency () {
            try {
                const res = await totalApi.statisticsEfficiency({ ...this.searchObj });
                const efficiency = res.data || {};

                if (res.code === 200) {
                    // 基于配置动态生成效率数据
                    this.efficiencyList = this.getEfficiencyConfig().map((item) => ({
                        ...item,
                        value: efficiency[item.key] || 0,
                    }));
                } else {
                    // 重置为默认值
                    this.efficiencyList = this.getEfficiencyConfig().map((item) => ({
                        ...item,
                        value: 0,
                    }));
                    this.$message.error(res.msg || this.$t('wuliu.workbench.fetchEfficiencyFailed'));
                }
            } catch (error) {
                // 异常时重置为默认值
                this.efficiencyList = this.getEfficiencyConfig().map((item) => ({
                    ...item,
                    value: 0,
                }));
                console.error('配送效率数据异常:', error);
                this.$message.error(this.$t('wuliu.workbench.fetchEfficiencyRetry'));
            }
        },

        // 获取单个图表趋势数据
        async getStatisticsLineChart (type) {
            try {
                const res = await totalApi.statisticsLineChart({
                    ...this.searchObj,
                    dataItem: type,
                });

                if (res.code === 200) {
                    const rawData = res.data || { xaxis: [], data: [] };
                    this.chartData[type].data = this.transformToKeyValue(rawData);
                } else {
                    this.chartData[type].data = {};
                    this.$message.error(res.msg || this.$t('wuliu.workbench.fetchTrendFailed', { title: this.getChartConfigMap()[type].title }));
                }
            } catch (error) {
                this.chartData[type].data = {};
                this.$message.error(this.$t('wuliu.workbench.fetchTrendRetry', { title: this.getChartConfigMap()[type].title }));
            }
        },

        // 转换数据格式：{xaxis: [], data: []} → {日期: 值}
        transformToKeyValue (source) {
            return source.xaxis.reduce((acc, key, index) => {
                acc[key] = source.data[index] || 0;
                return acc;
            }, {});
        },
        /**
         * 将分钟数转换为时:分:秒格式
         * @param {number} minutes - 分钟数（可含小数，如7.2）
         * @returns {string} 格式化后的时间字符串（如"0:07:12"）
        */
        formatMinutesToHMS (time) {
            let minutes = Number(time);
            // 处理非数字情况
            if (minutes === 0) {
                return '0:00:00';
            }

            // 总秒数 = 分钟数 × 60（保留整数秒）
            const totalSeconds = Math.floor(minutes * 60);

            // 计算时、分、秒
            const hours = Math.floor(totalSeconds / 3600);
            const remainingSeconds = totalSeconds % 3600;
            const mins = Math.floor(remainingSeconds / 60);
            const secs = remainingSeconds % 60;

            // 补零处理（确保分和秒为两位数）
            const formattedMins = mins.toString().padStart(2, '0');
            const formattedSecs = secs.toString().padStart(2, '0');

            // 拼接结果（小时不补零，如1小时7分12秒 → "1:07:12"）
            return `${hours} : ${formattedMins} : ${formattedSecs}`;
        },

    },
    async mounted () {
        // 初始化加载数据
        await this.getDeliverySiteList();
        this.fetchAllData();
    },
};
</script>

<style lang="scss" scoped>
// 变量集中管理
$bgColor2: #071150;
$borderColor: #1a47a4;
$highlightColor: #34C6D0;
$cardColor0: #3D8AE5;
$cardColor1: #34C6D0;

.workbench-main {
    height: calc(100vh - 106px);
    position: relative;
    background-color: $bgColor2;
    color: #ffffff;
    z-index: 1;
    margin: 10px;
    box-sizing: border-box;

    .data-cell {
        margin-top: 10px;
        box-sizing: border-box;

        .data-statistics {
            display: flex;
            gap: 16px;
            margin-bottom: 20px;

            .statistics-item {
                flex: 1;
                height: 104px;
                background-size: 100% 100%;
                padding: 10px;
                position: relative;

                .statistics-txt {
                    font-size: 14px;
                }

                .statistics-value {
                    font-size: 24px;
                    font-weight: bold;
                    margin-top: 8px;
                    position: absolute;
                    left: 10px;
                    bottom: 15px;
                }
            }
        }
    }
}

.overview-bg-1 {
    background-image: url('./images/overview-bg-1.png');
}

.overview-bg-2 {
    background-image: url('./images/overview-bg-2.png');
}

.overview-bg-3 {
    background-image: url('./images/overview-bg-3.png');
}

.overview-bg-4 {
    background-image: url('./images/overview-bg-4.png');
}

.overview-bg-5 {
    background-image: url('./images/overview-bg-5.png');
}
</style>
